// client/src/coms/voip/voiceclient_parts/video.js
import { Track, VideoPreset, createLocalVideoTrack } from 'livekit-client';
import { getPreferredVideoInputDeviceId } from '../../video/videoDevicePrefs';
import { captureScreenShareMedia, getScreenShareAudioSource } from '../screenShareAudioManager';
import { voiceState } from './state';


function findLocalVideoPublication(room) {
  const r = room || voiceState.activeRoom;
  if (!r || !r.localParticipant) return null;

  try {
    const pubs = Array.from(r.localParticipant.videoTrackPublications?.values() || []);
    for (const pub of pubs) {
      if (!pub) continue;
      if (pub.track && pub.track.mediaStreamTrack) return pub;
    }
  } catch {}

  return null;
}

export function getLocalCameraOn(room) {
  const pub = findLocalVideoPublication(room);
  if (!pub) return false;

  try {
    if (pub.isMuted === true) return false;
  } catch {}

  return true;
}

export function getLocalScreenOn(room) {
  const r = room || voiceState.activeRoom;
  if (!r || !r.localParticipant) return false;

  try {
    const pubs = Array.from(r.localParticipant.videoTrackPublications?.values() || []);
    for (const pub of pubs) {
      if (!pub) continue;
      if (pub.isMuted) continue;
      const src = pub.source || (pub.track ? pub.track.source : null);
      if (src === Track.Source.ScreenShare) return true;
    }
  } catch {}

  return false;
}

export function getLocalVideoMode(room) {
  const r = room || voiceState.activeRoom;
  if (!r || !r.localParticipant) return 'none';

  let hasCam = false;
  let hasScreen = false;

  try {
    const pubs = Array.from(r.localParticipant.videoTrackPublications?.values() || []);
    for (const pub of pubs) {
      if (!pub) continue;
      if (pub.isMuted) continue;
      const src = pub.source || (pub.track ? pub.track.source : null);
      if (src === Track.Source.Camera) hasCam = true;
      if (src === Track.Source.ScreenShare) hasScreen = true;
    }
  } catch {}

  if (hasCam && hasScreen) return 'both';
  if (hasScreen) return 'screen';
  if (hasCam) return 'camera';
  return 'none';
}

export function getLocalVideoErr() {
  return String(voiceState.lastVideoErr || '');
}

export async function enableLocalCamera(room) {
  voiceState.lastVideoErr = '';
  const r = room || voiceState.activeRoom;
  if (!r || !r.localParticipant) return { ok: false, reason: 'no_room' };

  try {
    const inputId = String(getPreferredVideoInputDeviceId() || '').trim();

    const constraints = {
      width: { ideal: 1280 },
      height: { ideal: 720 },
      frameRate: { ideal: 30, max: 30 },
    };

    const track = inputId
      ? await createLocalVideoTrack({ deviceId: inputId, ...constraints })
      : await createLocalVideoTrack({ ...constraints });

    voiceState.activeCameraTrack = track.mediaStreamTrack || track;
    let pub = null;

    try {
      pub = await r.localParticipant.publishTrack(track.mediaStreamTrack || track, { source: Track.Source.Camera });
    } catch {
      pub = null;
      await r.localParticipant.publishTrack(track.mediaStreamTrack || track, { source: Track.Source.Camera });
    }

    voiceState.activeCameraPub = pub;

    if (!voiceState.activeCameraPub) {
      try {
        voiceState.activeCameraPub = r.localParticipant.getTrackPublication(track.mediaStreamTrack || track) || null;
      } catch {
        voiceState.activeCameraPub = null;
      }
    }

    return { ok: true };
  } catch (e) {
    voiceState.lastVideoErr = String((e && e.message) || e || 'camera_failed');
    return { ok: false, reason: voiceState.lastVideoErr };
  }
}

export async function disableLocalCamera(room) {
  voiceState.lastVideoErr = '';
  const r = room || voiceState.activeRoom;
  if (!r || !r.localParticipant) return { ok: false, reason: 'no_room' };

  try {
    if (voiceState.activeCameraPub) {
      try {
        r.localParticipant.unpublishTrack(voiceState.activeCameraPub.track || voiceState.activeCameraTrack, false);
      } catch {}
    } else if (voiceState.activeCameraTrack) {
      try {
        r.localParticipant.unpublishTrack(voiceState.activeCameraTrack, false);
      } catch {}
    }

    try {
      if (voiceState.activeCameraTrack && typeof voiceState.activeCameraTrack.stop === 'function') voiceState.activeCameraTrack.stop();
    } catch {}

    voiceState.activeCameraTrack = null;
    voiceState.activeCameraPub = null;

    return { ok: true };
  } catch (e) {
    voiceState.lastVideoErr = String((e && e.message) || e || 'camera_disable_failed');
    return { ok: false, reason: voiceState.lastVideoErr };
  }
}

async function captureScreen() {
  if (!navigator.mediaDevices || typeof navigator.mediaDevices.getDisplayMedia !== 'function') {
    throw new Error('screen_share_unsupported');
  }

  let stream = null;

  try {
    const captured = await captureScreenShareMedia();
    stream = captured.stream;

    const track = captured.track || null;
    const audioTrack = captured.audioTrack || null;
    if (!track) {
      try {
        for (const t of stream.getTracks()) t.stop();
      } catch {}
      throw new Error('screen_track_missing');
    }

    try {
      track.contentHint = 'detail';
    } catch {}

    return { stream, track, audioTrack };
  } catch (e) {
    try {
      if (stream) {
        for (const t of stream.getTracks()) t.stop();
      }
    } catch {}

    throw e;
  }
}

export async function enableLocalScreen(room) {
  voiceState.lastVideoErr = '';
  const r = room || voiceState.activeRoom;
  if (!r || !r.localParticipant) return { ok: false, reason: 'no_room' };

  try {
    const { stream, track, audioTrack } = await captureScreen();

    voiceState.activeScreenStream = stream;
    voiceState.activeScreenTrack = track;

    let pub = null;

    try {
      pub = await r.localParticipant.publishTrack(track, { source: Track.Source.ScreenShare });
    } catch {
      pub = null;
      await r.localParticipant.publishTrack(track, { source: Track.Source.ScreenShare });
    }

    if (audioTrack) {
      try {
        const audioSource = getScreenShareAudioSource();
        if (audioSource) await r.localParticipant.publishTrack(audioTrack, { source: audioSource });
        else await r.localParticipant.publishTrack(audioTrack, { source: Track.Source.ScreenShareAudio });
      } catch {}
    }

    voiceState.activeScreenPub = pub;

    try {
      track.onended = () => {
        try {
          disableLocalScreen(r);
        } catch {}
      };
    } catch {}

    return { ok: true };
  } catch (e) {
    voiceState.lastVideoErr = String((e && e.message) || e || 'screen_failed');
    return { ok: false, reason: voiceState.lastVideoErr };
  }
}

export async function disableLocalScreen(room) {
  voiceState.lastVideoErr = '';
  const r = room || voiceState.activeRoom;
  if (!r || !r.localParticipant) return { ok: false, reason: 'no_room' };

  try {
    if (voiceState.activeScreenPub) {
      try {
        r.localParticipant.unpublishTrack(voiceState.activeScreenPub.track || voiceState.activeScreenTrack, false);
      } catch {}
    } else if (voiceState.activeScreenTrack) {
      try {
        r.localParticipant.unpublishTrack(voiceState.activeScreenTrack, false);
      } catch {}
    }

    try {
      if (voiceState.activeScreenStream && typeof voiceState.activeScreenStream.getAudioTracks === 'function') {
        for (const t of voiceState.activeScreenStream.getAudioTracks()) {
          try {
            r.localParticipant.unpublishTrack(t, false);
          } catch {}
        }
      }
    } catch {}

    try {
      if (voiceState.activeScreenTrack && typeof voiceState.activeScreenTrack.stop === 'function') voiceState.activeScreenTrack.stop();
    } catch {}

    try {
      if (voiceState.activeScreenStream && typeof voiceState.activeScreenStream.getTracks === 'function') {
        for (const t of voiceState.activeScreenStream.getTracks()) {
          try {
            t.stop();
          } catch {}
        }
      }
    } catch {}

    voiceState.activeScreenStream = null;
    voiceState.activeScreenTrack = null;
    voiceState.activeScreenPub = null;

    return { ok: true };
  } catch (e) {

    voiceState.lastVideoErr = String((e && e.message) || e || 'screen_disable_failed');
    return { ok: false, reason: voiceState.lastVideoErr };
  }
}

export async function setLocalVideoMode(mode, room) {
  const want = String(mode || 'none');
  const r = room || voiceState.activeRoom;
  if (!r || !r.localParticipant) return { ok: false, reason: 'no_room' };

  const current = getLocalVideoMode(r);
  if (current === want) return { ok: true };

  if (want === 'none') {
    const a = await disableLocalCamera(r);
    const b = await disableLocalScreen(r);
    return { ok: !!(a.ok && b.ok), reason: !a.ok ? a.reason : !b.ok ? b.reason : '' };
  }

  if (want === 'camera') {
    const b = await disableLocalScreen(r);
    const a = await enableLocalCamera(r);
    return { ok: !!(a.ok && b.ok), reason: !a.ok ? a.reason : !b.ok ? b.reason : '' };
  }

  if (want === 'screen') {
    const a = await disableLocalCamera(r);
    const b = await enableLocalScreen(r);
    return { ok: !!(a.ok && b.ok), reason: !a.ok ? a.reason : !b.ok ? b.reason : '' };
  }

  if (want === 'both') {
    const a = current === 'screen' ? { ok: true } : await enableLocalCamera(r);
    const b = current === 'camera' ? { ok: true } : await enableLocalScreen(r);
    return { ok: !!(a.ok && b.ok), reason: !a.ok ? a.reason : !b.ok ? b.reason : '' };
  }

  return { ok: false, reason: 'bad_mode' };
}

export async function republishVideoTracks(room) {
  const r = room || voiceState.activeRoom;
  if (!r || !r.localParticipant) return;

  try {
    if (voiceState.activeCameraTrack) {
      let pub = null;
      try {
        pub = await r.localParticipant.publishTrack(voiceState.activeCameraTrack, { source: Track.Source.Camera });
      } catch {
        pub = null;
        await r.localParticipant.publishTrack(voiceState.activeCameraTrack, { source: Track.Source.Camera });
      }
      voiceState.activeCameraPub = pub;
    }
  } catch {}

  try {
    if (voiceState.activeScreenTrack) {
      let pub = null;
      try {
        pub = await r.localParticipant.publishTrack(voiceState.activeScreenTrack, { source: Track.Source.ScreenShare });
      } catch {
        pub = null;
        await r.localParticipant.publishTrack(voiceState.activeScreenTrack, { source: Track.Source.ScreenShare });
      }
      voiceState.activeScreenPub = pub;
    }
  } catch {}

  try {
    if (voiceState.activeScreenStream && typeof voiceState.activeScreenStream.getAudioTracks === 'function') {
      for (const t of voiceState.activeScreenStream.getAudioTracks()) {
        try {
          const audioSource = getScreenShareAudioSource();
          if (audioSource) await r.localParticipant.publishTrack(t, { source: audioSource });
          else await r.localParticipant.publishTrack(t, { source: Track.Source.ScreenShareAudio });
        } catch {}
      }
    }
  } catch {}

}

export function pickParticipantVideoTrack(participant) {
  const p = participant || null;
  if (!p) return null;

  try {
    const pubs = Array.from(p.videoTrackPublications?.values() || []);
    for (const pub of pubs) {
      if (!pub) continue;
      if (pub.isMuted) continue;
      if (pub.track && pub.track.mediaStreamTrack) return pub.track;
    }
  } catch {}

  return null;
}

// Publish options for a screen track on the SFU. Without an explicit encoding livekit-client picks its
// stock screen-share preset (1080p at 15 fps), so a 30/60 fps share lost its frame rate on the SFU. A low
// 360p layer (at most 15 fps) lets thumbnails, hidden tiles and phones subscribe cheaply (adaptive stream).
export function screenSharePublishOptions(encoding) {
  const maxBitrate = Math.max(300000, Math.round(Number(encoding && encoding.maxBitrate) || 2500000));
  const maxFramerate = Math.max(5, Math.min(60, Math.round(Number(encoding && encoding.maxFramerate) || 30)));
  return {
    source: Track.Source.ScreenShare,
    simulcast: true,
    screenShareEncoding: { maxBitrate, maxFramerate, priority: 'high' },
    screenShareSimulcastLayers: [new VideoPreset(640, 360, Math.min(400000, Math.round(maxBitrate / 6)), Math.min(15, maxFramerate))],
    degradationPreference: maxFramerate >= 30 ? 'maintain-framerate' : 'maintain-resolution',
  };
}

export async function publishExistingScreenTrackToLiveKit(room, track, audioTrack, encoding) {
  voiceState.lastVideoErr = '';
  const r = room || voiceState.activeRoom;
  const t = track || null;
  const a = audioTrack || null;
  if (!r || !r.localParticipant) return { ok: false, reason: 'no_room' };
  if (!t) return { ok: false, reason: 'no_track' };

  try {
    let pub = null;
    let audioPub = null;

    try {
      pub = await r.localParticipant.publishTrack(t, screenSharePublishOptions(encoding));
    } catch {
      pub = null;
      await r.localParticipant.publishTrack(t, { source: Track.Source.ScreenShare });
    }

    if (a) {
      try {
        const audioSource = getScreenShareAudioSource();
        if (audioSource) audioPub = await r.localParticipant.publishTrack(a, { source: audioSource });
        else audioPub = await r.localParticipant.publishTrack(a, { source: Track.Source.ScreenShareAudio });
      } catch {
        audioPub = null;
      }
    }

    return { ok: true, pub: pub || null, audioPub: audioPub || null };
  } catch (e) {
    voiceState.lastVideoErr = String((e && e.message) || e || 'screen_publish_failed');
    return { ok: false, reason: voiceState.lastVideoErr };
  }
}

export async function unpublishScreenTrackFromLiveKit(room, pubOrTrack) {
  voiceState.lastVideoErr = '';
  const r = room || voiceState.activeRoom;
  if (!r || !r.localParticipant) return { ok: false, reason: 'no_room' };

  const v = pubOrTrack || null;
  const t = v && v.track ? v.track : v;

  try {
    if (t) {
      try {
        r.localParticipant.unpublishTrack(t, false);
      } catch {}
    }

    try {
      if (voiceState.activeScreenStream && typeof voiceState.activeScreenStream.getAudioTracks === 'function') {
        for (const at of voiceState.activeScreenStream.getAudioTracks()) {
          try {
            r.localParticipant.unpublishTrack(at, false);
          } catch {}
        }
      }
    } catch {}

    // Screen audio published from a direct share moved to the SFU is not on activeScreenStream;
    // without this it stayed live after the share stopped.
    try {
      for (const p of Array.from(r.localParticipant.trackPublications.values())) {
        if (p && p.source === Track.Source.ScreenShareAudio && p.track) {
          try {
            r.localParticipant.unpublishTrack(p.track, false);
          } catch {}
        }
      }
    } catch {}

    return { ok: true };
  } catch (e) {
    voiceState.lastVideoErr = String((e && e.message) || e || 'screen_unpublish_failed');
    return { ok: false, reason: voiceState.lastVideoErr };
  }
}
